import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import PositionFilters from '../components/PositionFilters/PositionFilters';
import LongShortBar from '../components/LongShortBar/LongShortBar';
import HyperliquidPositionsTable from '../components/HyperliquidPositionsTable/HyperliquidPositionsTable';
import PositionDebugPanel from '../components/PositionDebugPanel/PositionDebugPanel';
import { usePositionFilters } from '../hooks/usePositionFilters';
import { POSITION_FILTER_DEFAULTS } from '../hooks/usePositionFilters';
import { useHyperliquidPositions } from '../hooks/useHyperliquidPositions';
import './HyperliquidPositionsPage.css';

/**
 * Hyperliquid Positions Page
 * Real-time feed of large positions on Hyperliquid DEX
 * Following TradingPage.jsx structure and grey-black theme
 */
function HyperliquidPositionsPage() {
  const { t } = useTranslation();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsTestMode(params.get('isTestMode') === 'true');
  }, []);

  // Position filters hook
  const {
    filters,
    setMinPositionSize,
    setSymbols,
    setSide,
    setUserSearch,
    resetToDefaults,
    formatDollarAmount,
    hasActiveFilters,
    activeFilterCount
  } = usePositionFilters();

  // WebSocket connection for positions
  const {
    isConnected,
    isSubscribed,
    subscriptionError,
    positions,
    longShortStats,
    simulatePositionOpened,
    simulatePositionUpdate,
    simulatePositionClosed
  } = useHyperliquidPositions(filters, {
    enabled: true
  });

  // Client-side filtering of positions to match current filters
  const filteredPositions = useMemo(() => {
    return positions.filter(p => {
      // Min position size
      if (p.positionValue < filters.minPositionSize) return false;
      // Side filter
      if (filters.side !== 'all' && p.side !== filters.side) return false;
      // Symbols filter
      if (filters.symbols && filters.symbols.length > 0) {
        if (!filters.symbols.includes(p.symbol)) return false;
      }
      // User search
      if (filters.userSearch && filters.userSearch.trim()) {
        const search = filters.userSearch.trim().toLowerCase();
        if (!p.user?.toLowerCase().includes(search)) return false;
      }
      return true;
    });
  }, [positions, filters]);

  // Build filter chips for active filters strip
  const filterChips = useMemo(() => {
    const chips = [];
    if (filters.minPositionSize !== POSITION_FILTER_DEFAULTS.minPositionSize) {
      chips.push({ key: 'size', label: `>${formatDollarAmount(filters.minPositionSize)}`, type: 'default' });
    }
    if (filters.side !== POSITION_FILTER_DEFAULTS.side) {
      chips.push({ key: 'side', label: filters.side === 'long' ? t('hyperliquid.long') : t('hyperliquid.short'), type: filters.side });
    }
    if (filters.symbols && filters.symbols.length > 0) {
      chips.push({ key: 'symbols', label: filters.symbols.join(', '), type: 'default' });
    }
    if (filters.userSearch && filters.userSearch.trim().length > 0) {
      chips.push({ key: 'user', label: `${filters.userSearch.trim().slice(0, 10)}...`, type: 'default' });
    }
    return chips;
  }, [filters, formatDollarAmount, t]);

  return (
    <div className="hyperliquid-page">
      <SEO page="hyperliquid" />
      {/* Header */}
      <Header />

      {/* Position Debug Panel - shown when ?isTestMode=true */}
      {isTestMode && (
        <PositionDebugPanel
          onSimulatePositionOpened={simulatePositionOpened}
          onSimulatePositionUpdate={simulatePositionUpdate}
          onSimulatePositionClosed={simulatePositionClosed}
          isConnected={isConnected}
          isSubscribed={isSubscribed}
        />
      )}

      {/* Main Content */}
      <div className="container-fluid">
        <div className="row">
          {/* Left Sidebar - Filters */}
          <div className="col-lg-3 col-md-4">
            <div className="filters-sidebar">
              {/* Mobile toggle bar */}
              <div
                className="filters-toggle-bar"
                onClick={() => setFiltersExpanded(prev => !prev)}
              >
                <div className="d-flex align-items-center gap-2">
                  <span>{t('hyperliquid.activeFilters')}</span>
                  {activeFilterCount > 0 && (
                    <span className="filters-count-badge">{activeFilterCount}</span>
                  )}
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  className={`filters-toggle-arrow ${filtersExpanded ? 'expanded' : ''}`}
                >
                  <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"/>
                </svg>
              </div>

              {/* Collapsible filters wrapper */}
              <div className={`filters-collapsible ${filtersExpanded ? 'expanded' : ''}`}>
                {/* Position Filters */}
                <PositionFilters
                  filters={filters}
                  setMinPositionSize={setMinPositionSize}
                  setSide={setSide}
                  setSymbols={setSymbols}
                  setUserSearch={setUserSearch}
                  hasActiveFilters={hasActiveFilters}
                />

                {/* Reset Button */}
                <div className="mb-3">
                  <button
                    className="btn btn-secondary btn-sm w-100"
                    onClick={resetToDefaults}
                  >
                    {t('hyperliquid.resetFilters')}
                  </button>
                </div>

                {/* Hyperliquid Promo */}
                <a href="https://app.hyperliquid.xyz/join/TRADERMAP" target="_blank" rel="noopener noreferrer" className="hl-sidebar-promo">
                  <span className="hl-sidebar-promo-logo">HYPERLIQUID</span>
                  <span className="hl-sidebar-promo-badge">4% FEE DISCOUNT</span>
                  <p className="hl-sidebar-promo-text">Trade crypto, FX, Nasdaq, S&P 500, commodities, oil and more — all in one place with some of the lowest fees in the market.</p>
                  <p className="hl-sidebar-promo-cta">Sign up through our link and get 4% off on fees. {'\u2192'}</p>
                </a>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="col-lg-9 col-md-8">
            {/* Page Title */}
            <div className="page-title-section mb-4">
              <h1 className="page-title">{t('hyperliquid.title')}</h1>
              <p className="page-subtitle">
                {t('hyperliquid.pageSubtitle')}
              </p>
            </div>

            {/* Long/Short Distribution Bar */}
            <LongShortBar
              longCount={longShortStats.longCount}
              shortCount={longShortStats.shortCount}
              longValue={longShortStats.longValue}
              shortValue={longShortStats.shortValue}
            />

            {/* Active Filters Strip */}
            {hasActiveFilters && (
              <div className="active-filters-strip">
                <div className="active-filters-chips">
                  {filterChips.map(chip => (
                    <span key={chip.key} className={`filter-chip ${chip.type}`}>
                      {chip.label}
                    </span>
                  ))}
                </div>
                <button
                  className="active-filters-clear"
                  onClick={resetToDefaults}
                >
                  {t('hyperliquid.clearAll')}
                </button>
              </div>
            )}

            {/* Positions Table */}
            <HyperliquidPositionsTable
              positions={filteredPositions}
              loading={!isConnected && positions.length === 0}
              isConnected={isConnected}
              hasActiveFilters={hasActiveFilters}
              onResetFilters={resetToDefaults}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default HyperliquidPositionsPage;
